import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

// Tipos para los parámetros desencriptados
interface DecryptedParams {
  permiteGuardar: boolean;
  idUsuario: string;
  /** Link del ATLAS: el visor oculta los datos del paciente (estudio anonimo). */
  anonimo: boolean;
  /** Datos que SI se pueden mostrar en un enlace anonimo. */
  edadAnios: number | null;
  sexo: string;
  fechaEstudio: string;
  institucionNombre: string;
  institucionImagen: string;
}

// Tipo para el contexto
interface EncryptedParamsContextType {
  params: DecryptedParams | null;
  isLoading: boolean;
  error: string | null;
  errorTitle: string | null;
  setSearchParams: (searchParams: URLSearchParams) => void;
}

// Crear el contexto
const EncryptedParamsContext = createContext<EncryptedParamsContextType | null>(null);

// URL del backend PACS (validación de tokens de links compartidos).
// Configurable por app-config: window.config.customConfig.backendUrl
// (config/dev.js = backend LOCAL; config/subdirectory.js = producción).
// Fallback: producción (comportamiento histórico).
function getBackendBase(): string {
  try {
    const w = window as any;
    const custom = w?.config?.customConfig;
    if (custom && typeof custom.backendUrl === 'string' && custom.backendUrl.trim() !== '') {
      return custom.backendUrl.replace(/\/+$/, '');
    }
  } catch (_) {
    // Sin acceso a window.config: se usa producción
  }
  return 'https://pacs.ipse.com.ec';
}
const VALIDAR_LINK_URL = `${getBackendBase()}/Ajax/Aj_CompartirLinks.php`;

// Clave localStorage para el ID de navegador (vínculo de un solo uso)
const NAVEGADOR_KEY = 'ipse_link_device_id';

// Clave localStorage para la expiración del token (comparación local)
const EXPIRACION_PREFIX = 'ipse_link_exp_';

// Claves sessionStorage para restaurar el acceso en recargas del MISMO
// navegador (el visor se recarga al recuperar el foco y OHIF limpia el token
// de la URL; sin esto el doctor veía "Enlace no válido" al regresar).
const TOKEN_KEY = 'ipse_link_token';
const PERMISO_KEY = 'ipse_link_permiso';
const IDUSUARIO_KEY = 'ipse_link_idusuario';
const ANONIMO_KEY = 'ipse_link_anonimo';
const EDAD_KEY = 'ipse_link_edad_anios';
const SEXO_KEY = 'ipse_link_sexo';
const FECHA_ESTUDIO_KEY = 'ipse_link_fecha_estudio';
const INST_NOMBRE_KEY = 'ipse_link_institucion_nombre';
const INST_IMAGEN_KEY = 'ipse_link_institucion_imagen';

/**
 * Genera (o recupera) el ID persistente de este navegador para el vínculo
 * de un solo uso de los links compartidos.
 * Si la URL trae ?device=XXXX (la app móvil lo envía desde shared_preferences),
 * se usa ESE id como navegador_id: así la recreación del WebView (girar
 * pantalla, cambio de memoria) no cambia el id y no rompe el vínculo.
 */
function obtenerNavegadorId(searchParams?: URLSearchParams): string {
  try {
    // 1) Device explícito en la URL (app móvil / WebView estable)
    if (searchParams) {
      const device = searchParams.get('device');
      if (device && device.length >= 8) {
        localStorage.setItem(NAVEGADOR_KEY, device);
        return device;
      }
    }
    // 2) Id persistente de este navegador
    const existente = localStorage.getItem(NAVEGADOR_KEY);
    if (existente) {
      return existente;
    }
    const nuevo =
      'dev-' +
      Math.random().toString(36).substring(2, 10) +
      Date.now().toString(36);
    localStorage.setItem(NAVEGADOR_KEY, nuevo);
    return nuevo;
  } catch (e) {
    return '';
  }
}

/**
 * Restaura el acceso desde sessionStorage cuando la URL ya no trae token
 * (recarga del MISMO navegador). Verifica la expiración guardada localmente
 * SIN consultar el backend.
 * Devuelve: los datos restaurados | 'expirado' (venció) | null (no hay sesión).
 */
function restaurarSesionLocal():
  | {
      token: string;
      permiso: string;
      idUsuario: string;
      anonimo: boolean;
      edadAnios: number | null;
      sexo: string;
      fechaEstudio: string;
      institucionNombre: string;
      institucionImagen: string;
    }
  | 'expirado'
  | null {
  try {
    // Leer de sessionStorage; respaldo en localStorage (sobrevive recreaciones
    // del WebView, donde sessionStorage se pierde).
    const token =
      sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY) || '';
    if (!token) {
      return null;
    }
    // Verificar expiración local: si venció, limpiar y reportar.
    const expiracionGuardada = localStorage.getItem(EXPIRACION_PREFIX + token);
    if (expiracionGuardada) {
      const expMs = new Date(expiracionGuardada.replace(' ', 'T')).getTime();
      if (!isNaN(expMs) && Date.now() > expMs) {
        limpiarSesionLocal();
        return 'expirado';
      }
    }
    const permiso =
      sessionStorage.getItem(PERMISO_KEY) || localStorage.getItem(PERMISO_KEY) || '';
    if (!permiso) {
      return null;
    }
    const edadRaw =
      sessionStorage.getItem(EDAD_KEY) || localStorage.getItem(EDAD_KEY) || '';
    const edadNum = edadRaw === '' ? NaN : Number(edadRaw);
    return {
      token,
      permiso,
      idUsuario:
        sessionStorage.getItem(IDUSUARIO_KEY) ||
        localStorage.getItem(IDUSUARIO_KEY) ||
        'unknown',
      anonimo:
        (sessionStorage.getItem(ANONIMO_KEY) || localStorage.getItem(ANONIMO_KEY)) === '1',
      edadAnios: Number.isFinite(edadNum) ? edadNum : null,
      sexo: sessionStorage.getItem(SEXO_KEY) || localStorage.getItem(SEXO_KEY) || '',
      fechaEstudio:
        sessionStorage.getItem(FECHA_ESTUDIO_KEY) ||
        localStorage.getItem(FECHA_ESTUDIO_KEY) ||
        '',
      institucionNombre:
        sessionStorage.getItem(INST_NOMBRE_KEY) ||
        localStorage.getItem(INST_NOMBRE_KEY) ||
        '',
      institucionImagen:
        sessionStorage.getItem(INST_IMAGEN_KEY) ||
        localStorage.getItem(INST_IMAGEN_KEY) ||
        '',
    };
  } catch (e) {
    return null;
  }
}

/**
 * Limpia el token de la URL DESPUES de que la validacion termino.
 *
 * Si se limpia antes (o al mismo tiempo que se monta el validador), React
 * Router vuelve a renderizar sin token y el validador responde "Enlace no
 * valido" aunque el enlace sea bueno. Por eso la limpieza vive aqui, no en
 * updateAuthServiceAndCleanUrl.
 */
function limpiarTokenDeUrl(): void {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('token');
    if (url.hash && url.hash.indexOf('?') >= 0) {
      const [rutaHash, consulta] = url.hash.split('?');
      const paramsHash = new URLSearchParams(consulta);
      paramsHash.delete('token');
      const resto = paramsHash.toString();
      url.hash = resto ? `${rutaHash}?${resto}` : rutaHash;
    }
    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, '', url.toString());
    }
  } catch (e) {
    // Si el navegador no deja tocar la URL, el token sigue en storage.
  }
}

/**
 * Limpia la sesión local del link (token inválido/expirado/revocado).
 */
function limpiarSesionLocal(): void {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(PERMISO_KEY);
    sessionStorage.removeItem(IDUSUARIO_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(PERMISO_KEY);
    localStorage.removeItem(IDUSUARIO_KEY);
    sessionStorage.removeItem(ANONIMO_KEY);
    localStorage.removeItem(ANONIMO_KEY);
    sessionStorage.removeItem(EDAD_KEY);
    localStorage.removeItem(EDAD_KEY);
    sessionStorage.removeItem(SEXO_KEY);
    localStorage.removeItem(SEXO_KEY);
    sessionStorage.removeItem(FECHA_ESTUDIO_KEY);
    localStorage.removeItem(FECHA_ESTUDIO_KEY);
    sessionStorage.removeItem(INST_NOMBRE_KEY);
    localStorage.removeItem(INST_NOMBRE_KEY);
    sessionStorage.removeItem(INST_IMAGEN_KEY);
    localStorage.removeItem(INST_IMAGEN_KEY);
  } catch (e) {}
}

// Proveedor del contexto
export const EncryptedParamsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [params, setParams] = useState<DecryptedParams | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorTitle, setErrorTitle] = useState<string | null>(null);

  // Evita re-validar en el mismo montaje (el useEffect del validador puede
  // re-dispararse si cambian las referencias; esto corta el bucle).
  const validadoRef = useRef(false);

  /**
   * Valida el token UNA sola vez por carga de página contra el backend.
   * El backend devuelve además la expiración, que se guarda en localStorage
   * para comparaciones locales posteriores SIN más consultas.
   */
  const validarTokenBackend = async (token: string, navegadorId: string): Promise<DecryptedParams> => {
    const paramsQuery = new URLSearchParams();
    paramsQuery.set('Requerimiento', 'ValidarLink');
    paramsQuery.set('token', token);
    if (navegadorId) {
      paramsQuery.set('navegador_id', navegadorId);
    }
    const url = `${VALIDAR_LINK_URL}?${paramsQuery.toString()}`;
    const response = await fetch(url, { method: 'GET' });
    const data = await response.json();
    if (!data || data.success !== true) {
      const motivo = data?.motivo || 'no_existe';
      let msg = 'El enlace no es válido o fue modificado.';
      let title = 'Enlace no válido';
      if (motivo === 'expirado') {
        msg = 'Este enlace ha expirado. Solicite uno nuevo al paciente o al centro médico.';
        title = 'Enlace expirado';
      } else if (motivo === 'revocado') {
        msg = 'Este enlace fue revocado por su creador.';
        title = 'Enlace revocado';
      } else if (motivo === 'agotado') {
        msg = 'Este enlace alcanzó el límite de visualizaciones permitido.';
        title = 'Límite alcanzado';
      } else if (motivo === 'otro_navegador') {
        msg =
          'Este enlace ya fue abierto en otro navegador o dispositivo. Por seguridad, solo puede usarse en el primer navegador donde se abrió.';
        title = 'Enlace ya utilizado';
      }
      const err = new Error(msg) as Error & { title?: string };
      err.title = title;
      throw err;
    }

    // Guardar la expiración localmente (si el backend la envía) para no
    // volver a consultar en recargas: se compara con la fecha actual.
    if (data.expira_en) {
      try {
        localStorage.setItem(EXPIRACION_PREFIX + token, data.expira_en);
      } catch (e) {}
    }

    // Persistir token + permisos en sessionStorage Y localStorage: en recargas
    // del MISMO navegador (p.ej. auto-reload por pérdida de foco) se restaura
    // el acceso sin volver a consultar el backend. El localStorage es el
    // respaldo que sobrevive a la recreación del WebView (girar pantalla).
    try {
      sessionStorage.setItem(TOKEN_KEY, token);
      sessionStorage.setItem(PERMISO_KEY, data.permiso === 'guardar' ? 'guardar' : 'ver');
      sessionStorage.setItem(IDUSUARIO_KEY, String(data.id_usuario ?? 'unknown'));
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(PERMISO_KEY, data.permiso === 'guardar' ? 'guardar' : 'ver');
      localStorage.setItem(IDUSUARIO_KEY, String(data.id_usuario ?? 'unknown'));
      const marcaAnonimo = data.anonimo ? '1' : '0';
      sessionStorage.setItem(ANONIMO_KEY, marcaAnonimo);
      localStorage.setItem(ANONIMO_KEY, marcaAnonimo);

      const edad = data.edad_anios === null || data.edad_anios === undefined
        ? ''
        : String(data.edad_anios);
      const sexo = String(data.sexo ?? '');
      const fechaEstudio = String(data.fecha_estudio ?? '');
      const instNombre = String(data.institucion_nombre ?? '');
      const instImagen = String(data.institucion_imagen ?? '');
      sessionStorage.setItem(EDAD_KEY, edad);
      localStorage.setItem(EDAD_KEY, edad);
      sessionStorage.setItem(SEXO_KEY, sexo);
      localStorage.setItem(SEXO_KEY, sexo);
      sessionStorage.setItem(FECHA_ESTUDIO_KEY, fechaEstudio);
      localStorage.setItem(FECHA_ESTUDIO_KEY, fechaEstudio);
      sessionStorage.setItem(INST_NOMBRE_KEY, instNombre);
      localStorage.setItem(INST_NOMBRE_KEY, instNombre);
      sessionStorage.setItem(INST_IMAGEN_KEY, instImagen);
      localStorage.setItem(INST_IMAGEN_KEY, instImagen);
    } catch (e) {}

    const edadNum = data.edad_anios === null || data.edad_anios === undefined
      ? NaN
      : Number(data.edad_anios);
    return {
      permiteGuardar: data.permiso === 'guardar',
      idUsuario: String(data.id_usuario ?? 'unknown'),
      anonimo: !!data.anonimo,
      edadAnios: Number.isFinite(edadNum) ? edadNum : null,
      sexo: String(data.sexo ?? ''),
      fechaEstudio: String(data.fecha_estudio ?? ''),
      institucionNombre: String(data.institucion_nombre ?? ''),
      institucionImagen: String(data.institucion_imagen ?? ''),
    };
  };

  const setSearchParams = useCallback((searchParams: URLSearchParams) => {
    // UNA validación por montaje: si ya se validó, no se vuelve a consultar.
    if (validadoRef.current) {
      return;
    }
    validadoRef.current = true;

    setIsLoading(true);
    setError(null);
    setErrorTitle(null);

    const token = searchParams.get('token');

    // SIN token en la URL: intentar restaurar el acceso desde sessionStorage.
    // Es el MISMO navegador recargando (p.ej. el auto-reload por pérdida de
    // foco del visor): OHIF limpió el token de la URL pero la sesión sigue.
    // Si no hay nada guardado (navegador nuevo / link pegado sin token), se
    // deniega el acceso.
    if (!token) {
      const restaurada = restaurarSesionLocal();
      if (restaurada === 'expirado') {
        setParams(null);
        setError('Este enlace ha expirado. Solicite uno nuevo al paciente o al centro médico.');
        setErrorTitle('Enlace expirado');
        setIsLoading(false);
        return;
      }
      if (restaurada) {
        setParams({
          permiteGuardar: restaurada.permiso === 'guardar',
          idUsuario: restaurada.idUsuario,
          anonimo: restaurada.anonimo,
          edadAnios: restaurada.edadAnios,
          sexo: restaurada.sexo,
          fechaEstudio: restaurada.fechaEstudio,
          institucionNombre: restaurada.institucionNombre,
          institucionImagen: restaurada.institucionImagen,
        });
        setIsLoading(false);
        return;
      }
      setParams(null);
      setError(
        'Este enlace no es válido o fue modificado. Solicite un enlace autorizado al paciente o al centro médico.'
      );
      setErrorTitle('Enlace no válido');
      setIsLoading(false);
      return;
    }

    // Comparación LOCAL de expiración: si el token ya venció según lo guardado
    // en esta máquina, no hace falta consultar el backend.
    try {
      const expiracionGuardada = localStorage.getItem(EXPIRACION_PREFIX + token);
      if (expiracionGuardada) {
        const expMs = new Date(expiracionGuardada.replace(' ', 'T')).getTime();
        if (!isNaN(expMs) && Date.now() > expMs) {
          limpiarSesionLocal();
          setParams(null);
          setError('Este enlace ha expirado. Solicite uno nuevo al paciente o al centro médico.');
          setErrorTitle('Enlace expirado');
          setIsLoading(false);
          return;
        }
      }
    } catch (e) {}

    const navegadorId = obtenerNavegadorId(searchParams);

    validarTokenBackend(token, navegadorId)
      .then(decryptedParams => {
        setParams(decryptedParams);
        // Recien ahora se puede limpiar la URL: la validacion ya corrio.
        window.setTimeout(limpiarTokenDeUrl, 0);
      })
      .catch(err => {
        limpiarSesionLocal();
        setParams(null);
        setError(err.message || 'El enlace no es válido.');
        setErrorTitle(err.title || 'Enlace no válido');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const contextValue: EncryptedParamsContextType = {
    params,
    isLoading,
    error,
    errorTitle,
    setSearchParams
  };

  return (
    <EncryptedParamsContext.Provider value={contextValue}>
      {children}
    </EncryptedParamsContext.Provider>
  );
};

// Hook para usar el contexto
export const useEncryptedParams = (): EncryptedParamsContextType => {
  const context = useContext(EncryptedParamsContext);
  if (!context) {
    throw new Error('useEncryptedParams must be used within an EncryptedParamsProvider');
  }
  return context;
};

// Hook simplificado para obtener solo los parámetros
export const useUserPermissions = () => {
  const { params, isLoading, error } = useEncryptedParams();

  return {
    permiteGuardar: params?.permiteGuardar ?? false,
    idUsuario: params?.idUsuario ?? null,
    anonimo: params?.anonimo ?? false,
    edadAnios: params?.edadAnios ?? null,
    sexo: params?.sexo ?? '',
    fechaEstudio: params?.fechaEstudio ?? '',
    institucionNombre: params?.institucionNombre ?? '',
    institucionImagen: params?.institucionImagen ?? '',
    isLoading,
    error
  };
};
